import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { ValidationError, SubmissionResult } from '../types/petParent';
import api from '../services/api';
import { petParentReportsAtom } from '../atoms/petParentReportAtom';
import { shelterAtom } from '../atoms/shelterAtom';
import { PaginatedPetParentReports } from '../types/petParentReports';
import { ParsedUploadData } from '../components/Modal/SpreadsheetUploadModal';
import axios, { AxiosError } from 'axios';
import { normalizeCalendarDateToDateOnlyString } from '../utils/dateUtils';
import { normalizePostalCode } from '../utils/postalCodeValidation';

let pendingPetParentReportsRequest: Promise<PaginatedPetParentReports> | null = null;
let pendingPetParentReportsRequestKey: string | null = null;

// Interface for the Excel data as returned by the backend
interface ExcelPetParentData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  petName: string;
  petSpecies: string;
  birthdate: string | Date;
  adoptionDate: string | Date;
  adoptionLocation: string;
  retailPartner?: string;
  preferredLanguage?: string;
}

const parseErrorPayloadMessage = async (error: unknown): Promise<string | undefined> => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const payload = error.response?.data;
  if (!payload) {
    return undefined;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (payload instanceof Blob) {
    try {
      const text = await payload.text();
      if (!text) {
        return undefined;
      }
      try {
        const parsed = JSON.parse(text) as { message?: string };
        return parsed.message ?? text;
      } catch {
        return text;
      }
    } catch {
      return undefined;
    }
  }

  if (typeof payload === 'object' && payload !== null) {
    if ('message' in payload && typeof payload.message === 'string') {
      return payload.message;
    }
    if ('error' in payload && typeof payload.error === 'string') {
      return payload.error;
    }
  }

  return undefined;
};

/**
 * Custom hook to read the reports state.
 * Returns the current reports or null if not loaded/error.
 */
export const usePetParentReportState = () => {
  const petParentReportsData = useAtomValue(petParentReportsAtom);
  return petParentReportsData;
};

/**
 * Custom hook to provide actions related to adoptions.
 * Provides functions to submit adoption reports from the backend API.
 */
export const usePetParentReportActions = () => {
  const setPetParentReports = useSetAtom(petParentReportsAtom);
  const shelter = useAtomValue(shelterAtom);

  // Function to trigger a fetch of pet parent reports from the backend
  const getPetParentReports = useCallback(
    async (filter?: string, page = 1, limit = 10) => {
      setPetParentReports(null); // Set to null while fetching
      let currentRequest: Promise<PaginatedPetParentReports> | null = null;
      try {
        // Build query params for pagination and optional filter
        let url = `/pet-parent-reports?page=${page}&limit=${limit}`;
        if (filter && filter !== 'all') {
          url += `&filter=${filter}`;
        }

        const requestKey = `${filter || 'all'}|${page}|${limit}`;
        if (pendingPetParentReportsRequest && pendingPetParentReportsRequestKey === requestKey) {
          const data = await pendingPetParentReportsRequest;
          setPetParentReports(data);
          return;
        }

        pendingPetParentReportsRequestKey = requestKey;
        currentRequest = (async () => {
          const response = await api.get<PaginatedPetParentReports>(url);
          return response.data;
        })();
        pendingPetParentReportsRequest = currentRequest;

        const data = await currentRequest;
        setPetParentReports(data);
      } catch (error) {
        console.error('Failed to fetch pet parent reports:', error);
        // Keep data null on failure
        setPetParentReports(null);
      } finally {
        if (currentRequest && pendingPetParentReportsRequest === currentRequest) {
          pendingPetParentReportsRequest = null;
          pendingPetParentReportsRequestKey = null;
        }
      }
    },
    [setPetParentReports]
  );

  // Function to submit pet parent report
  const submitPetParentReport = useCallback(
    async (
      timeframe_start: string,
      petParentData: ParsedUploadData[],
      submissionMethod: string = 'manual'
    ): Promise<SubmissionResult> => {
      try {
        // Format the data for submission - create an array of submissions
        const isCanadian = shelter?.country === 'CA';
        const submissionData = petParentData.map((data) => ({
          timeframe_start,
          petParent: {
            firstName: data.petParentInfo.firstName,
            lastName: data.petParentInfo.lastName,
            email: data.petParentInfo.email,
            phone: data.petParentInfo.phone,
            // Map simple frontend fields to backend billing address structure
            billingAddress1: data.petParentInfo.address,
            billingCity: data.petParentInfo.city,
            billingStateProvince: data.petParentInfo.province || data.petParentInfo.state,
            billingZipCode: isCanadian
              ? normalizePostalCode(data.petParentInfo.zip)
              : data.petParentInfo.zip,
            billingCountry: isCanadian ? 'CA' : 'US',
            preferredLanguage: data.petParentInfo.preferredLanguage || undefined,
          },
          pets: data.petInfos.map((pet) => {
            const normalizedBirthdate = normalizeCalendarDateToDateOnlyString(pet.birthdate);
            const normalizedAdoptionDate = normalizeCalendarDateToDateOnlyString(pet.adoptionDate);

            return {
              ...pet,
              birthdate: normalizedBirthdate ?? undefined,
              adoptionDate: normalizedAdoptionDate ?? '',
            };
          }),
          submissionDate: new Date().toISOString(),
          submissionMethod,
        }));

        const requestPayload = {
          timeframe_start,
          data: submissionData,
        };

        await api.post('/pet-parent-reports', requestPayload);

        // Return success
        return { success: true };
      } catch (error) {
        console.error('Failed to submit pet parent report:', error);
        return { success: false, error };
      }
    },
    [shelter]
  );

  // Function to upload spreadsheet
  const uploadPetParentReportSpreadsheet = useCallback(
    async (
      timeframe_start: string,
      file: File
    ): Promise<SubmissionResult & { data?: ParsedUploadData[] }> => {
      try {
        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('timeframe_start', timeframe_start);

        // Upload the file and get the parsed data back
        const response = await api.post('/pet-parent-reports/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Check for the data in the API response
        if (response.data?.success && response.data?.data) {
          // Backend returns an array of pet parent data, we need to transform it into our format
          const excelData = response.data.data as ExcelPetParentData[];
          const errors = (response.data.errors as ValidationError[]) || [];

          if (excelData.length > 0) {
            // Group pets by pet parent (same email, firstName, lastName) and track original row indices
            const groupedByPetParent: Record<
              string,
              { rows: ExcelPetParentData[]; originalIndices: number[] }
            > = {};

            excelData.forEach((row, index) => {
              // Create a key using a combination of email, firstName and lastName
              const petParentKey = `${row.email.toLowerCase()}_${row.firstName.toLowerCase()}_${row.lastName.toLowerCase()}`;

              if (!groupedByPetParent[petParentKey]) {
                groupedByPetParent[petParentKey] = { rows: [], originalIndices: [] };
              }

              groupedByPetParent[petParentKey].rows.push(row);
              // Add 1 to index because validation errors use 1-based row numbers (header row is row 1, data starts at row 2)
              groupedByPetParent[petParentKey].originalIndices.push(index + 2);
            });

            // Transform each pet parent group into a ParsedUploadData object
            const isCanadian = shelter?.country === 'CA';
            const parsedDataArray: ParsedUploadData[] = Object.values(groupedByPetParent).map(
              (petParentGroup) => {
                const firstRow = petParentGroup.rows[0];
                const originalIndices = petParentGroup.originalIndices;
                const regionCode = firstRow.state;

                // Find validation errors for this pet parent based on original row numbers
                const petParentErrors = errors.filter((error) =>
                  originalIndices.includes(error.row)
                );

                return {
                  petParentInfo: {
                    firstName: firstRow.firstName,
                    lastName: firstRow.lastName,
                    email: firstRow.email,
                    phone: firstRow.phone,
                    address: firstRow.address,
                    city: firstRow.city,
                    state: isCanadian ? '' : regionCode,
                    zip: firstRow.zip,
                    province: isCanadian ? regionCode : undefined,
                    preferredLanguage: firstRow.preferredLanguage,
                  },
                  petInfos: petParentGroup.rows.map((row: ExcelPetParentData) => ({
                    name: row.petName,
                    species: row.petSpecies,
                    birthdate: normalizeCalendarDateToDateOnlyString(row.birthdate),
                    adoptionDate: normalizeCalendarDateToDateOnlyString(row.adoptionDate),
                    adoptionLocation: row.adoptionLocation,
                    retailPartner: row.retailPartner,
                  })),
                  validationErrors: petParentErrors,
                };
              }
            );

            return {
              success: true,
              data: parsedDataArray,
            };
          }
        }

        // If no data was found or transformation failed, return success but no data
        return { success: true };
      } catch (error) {
        console.error('Failed to upload spreadsheet:', error);

        // Extract error message from Axios error response
        let errorMessage = 'Failed to upload spreadsheet';

        if (axios.isAxiosError(error)) {
          // It's an Axios error
          const axiosError = error as AxiosError;

          // Extract the error message from the response
          if (axiosError.response?.data) {
            const responseData = axiosError.response.data;

            if (typeof responseData === 'string') {
              errorMessage = responseData;
            } else if (typeof responseData === 'object' && responseData !== null) {
              // Look for common error message fields
              if ('message' in responseData) {
                errorMessage = responseData.message as string;
              } else if ('error' in responseData) {
                const errorData = responseData.error;
                errorMessage =
                  typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
              }
            }
          }

          // If we couldn't extract a specific message, use the status text
          if (errorMessage === 'Failed to upload spreadsheet' && axiosError.response?.statusText) {
            errorMessage = `Error: ${axiosError.response.status} ${axiosError.response.statusText}`;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        return { success: false, error: errorMessage };
      }
    },
    [shelter]
  );

  // Function to download template file in specified format
  const downloadTemplate = useCallback(async (format: 'xlsx' | 'csv' = 'xlsx') => {
    try {
      const response = await api.get(`/pet-parent-reports/template?format=${format}`, {
        responseType: 'blob', // Important: tell axios to treat response as binary data
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `pet-parent-template.${format}`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`${format.toUpperCase()} template download completed`);
      return { success: true };
    } catch (error) {
      console.error('Template download failed:', error);
      let errorMessage = 'Template download failed';

      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message ?? error.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }, []);

  const downloadInstructionsTemplate = useCallback(async () => {
    try {
      // 1. Call your NestJS endpoint
      // Ensure the URL matches the route you defined in your Controller
      const response = await api.get('/pet-parent-reports/download-instructions', {
        responseType: 'blob', // Crucial for PDF/Binary data
      });

      // 2. Create a Blob from the response data
      // We explicitly set the type to application/pdf
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // 3. Create a temporary object URL
      const url = window.URL.createObjectURL(blob);

      // 4. Create and trigger the hidden anchor tag
      const link = document.createElement('a');
      link.href = url;

      // This filename is what the user will see when saving
      link.download = 'Pet_Parent_Report_Instructions.pdf';

      document.body.appendChild(link);
      link.click();

      // 5. Clean up the DOM and memory
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('PDF instructions download completed');
      return { success: true };
    } catch (error) {
      console.error('PDF download failed:', error);
      let errorMessage = 'Failed to download instructions';

      if (axios.isAxiosError(error)) {
        errorMessage = (await parseErrorPayloadMessage(error)) ?? error.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }, [shelter]);

  const downloadOptinGuide = useCallback(async () => {
    try {
      // 1. Call your NestJS endpoint
      // Ensure the URL matches the route you defined in your Controller
      const response = await api.get('/pet-parent-reports/download-optin-guide', {
        responseType: 'blob', // Crucial for PDF/Binary data
      });

      // 2. Create a Blob from the response data
      // We explicitly set the type to application/pdf
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // 3. Create a temporary object URL
      const url = window.URL.createObjectURL(blob);

      // 4. Create and trigger the hidden anchor tag
      const link = document.createElement('a');
      link.href = url;

      // This filename is what the user will see when saving
      link.download = shelter?.is_petszel_enabled
        ? 'Automated Opt-In_Guide.pdf'
        : 'Manual Opt-In_Guide.pdf';

      document.body.appendChild(link);
      link.click();

      // 5. Clean up the DOM and memory
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('Opt-in Guide PDF download completed');
      return { success: true };
    } catch (error) {
      console.error('PDF download failed:', error);
      let errorMessage = 'Failed to download Opt-in Guide PDF';

      if (axios.isAxiosError(error)) {
        errorMessage = (await parseErrorPayloadMessage(error)) ?? error.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    }
  }, [shelter]);

  return {
    getPetParentReports,
    submitPetParentReport,
    uploadPetParentReportSpreadsheet,
    downloadTemplate,
    downloadInstructionsTemplate,
    downloadOptinGuide,
  };
};
